import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * RecordDialogContext
 * --------------------
 * Centralised stack-based controller for opening any entity's detail/edit/view UI
 * inside an overlay dialog instead of navigating to a separate route. The same
 * content component is used both here and by the (deprecated) full-page detail
 * routes, so behaviour is defined exactly once.
 *
 * Stack semantics (pure modals, no URL):
 *  - openRecord({ type, id, props? }) pushes onto the stack (does NOT change URL)
 *  - closeRecord() pops the top of the stack
 *  - closeAll() empties the stack
 *  - replaceTop({ type, id, props? }) replaces the top entry without animation
 *
 * The host (RecordDialogHost) renders the stack as a SINGLE overlay showing
 * the top entry with a breadcrumb trail — opening a related record navigates
 * in place rather than stacking another dialog. Esc / the back arrow pops one
 * level; the X and backdrop click close the whole trail (backdrop is
 * configurable per-call via props.disableBackdropClose).
 */

const RecordDialogContext = createContext(null);

let entryIdCounter = 0;
const nextEntryId = () => {
  entryIdCounter += 1;
  return `rec_${Date.now()}_${entryIdCounter}`;
};

export const RecordDialogProvider = ({ children }) => {
  const [stack, setStack] = useState([]);
  const stackRef = useRef(stack);
  stackRef.current = stack;

  const openRecord = useCallback((entry) => {
    if (!entry || !entry.type) return null;
    const newEntry = {
      key: nextEntryId(),
      type: entry.type,
      id: entry.id || null,
      props: entry.props || {},
      mode: entry.mode || 'view',
      onClose: entry.onClose || null
    };
    setStack((prev) => {
      // Avoid pushing a duplicate of the current top entry (rapid double click).
      const top = prev[prev.length - 1];
      if (top && top.type === newEntry.type && top.id === newEntry.id && top.mode === newEntry.mode) {
        return prev;
      }
      return [...prev, newEntry];
    });
    return newEntry.key;
  }, []);

  const closeRecord = useCallback((key) => {
    setStack((prev) => {
      if (!prev.length) return prev;
      if (key) {
        const idx = prev.findIndex((e) => e.key === key);
        if (idx === -1) return prev;
        const removed = prev[idx];
        if (removed?.onClose) {
          try { removed.onClose(); } catch (_) { /* ignore */ }
        }
        return prev.filter((e) => e.key !== key);
      }
      const top = prev[prev.length - 1];
      if (top?.onClose) {
        try { top.onClose(); } catch (_) { /* ignore */ }
      }
      return prev.slice(0, -1);
    });
  }, []);

  const closeAll = useCallback(() => {
    setStack((prev) => {
      prev.forEach((e) => {
        if (e?.onClose) {
          try { e.onClose(); } catch (_) { /* ignore */ }
        }
      });
      return [];
    });
  }, []);

  const replaceTop = useCallback((entry) => {
    if (!entry || !entry.type) return null;
    const newEntry = {
      key: nextEntryId(),
      type: entry.type,
      id: entry.id || null,
      props: entry.props || {},
      mode: entry.mode || 'view',
      onClose: entry.onClose || null
    };
    setStack((prev) => {
      if (!prev.length) return [newEntry];
      const next = prev.slice(0, -1);
      return [...next, newEntry];
    });
    return newEntry.key;
  }, []);

  // Esc on the document closes the top entry (MUI Dialog also handles this,
  // but we centralise it for non-Dialog hosts and to avoid dependency on focus).
  useEffect(() => {
    const handler = (event) => {
      if (event.key === 'Escape' && stackRef.current.length) {
        closeRecord();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [closeRecord]);

  const value = useMemo(() => ({
    stack,
    openRecord,
    closeRecord,
    closeAll,
    replaceTop
  }), [stack, openRecord, closeRecord, closeAll, replaceTop]);

  return (
    <RecordDialogContext.Provider value={value}>
      {children}
    </RecordDialogContext.Provider>
  );
};

export const useRecordDialog = () => {
  const ctx = useContext(RecordDialogContext);
  if (!ctx) {
    throw new Error('useRecordDialog must be used inside <RecordDialogProvider>.');
  }
  return ctx;
};

/**
 * Convenience helpers. Pass a result of useRecordDialog() (or just call
 * openRecord directly with the right shape).
 */
export const openClient = (open, id, props) => open({ type: 'client', id, props });
export const openContact = (open, id, props) => open({ type: 'contact', id, props });
export const openAsset = (open, id, props) => open({ type: 'asset', id, props });
export const openSite = (open, id, props) => open({ type: 'site', id, props });
export const openTicket = (open, id, props) => open({ type: 'ticket', id, props });
export const openProject = (open, id, props) => open({ type: 'project', id, props });
export const openLicense = (open, id, props) => open({ type: 'license', id, props });
export const openQuote = (open, id, props) => open({ type: 'quote', id, props });
export const openInvoice = (open, id, props) => open({ type: 'invoice', id, props });
export const openCredential = (open, id, props) => open({ type: 'credential', id, props });

export default RecordDialogContext;
